import QuestionTitle from '@/ui/components/inspectionReport/items/QuestionTitle.vue';
import { defineComponent, h, computed, inject } from 'vue';
import { ItemProps } from '@/ui/components/inspectionReport/types';
import isObject from 'lodash/isObject';
import { useInspectionReport } from '@/ui/components/inspectionReport/useInspectioReport';
import TableBox from '@/ui/components/inspectionReport/items/table/TableBox.vue';
import { ImageAsset } from '@/ui/components/asset/ImageAsset';
import { DocumentAsset } from '@/ui/components/asset/DocumentAsset';
import { hasFeature } from '@/ui/auth/auth';
import { t } from '@/ui/i18n/i18n';
import QuestionNotes from '@/ui/components/inspectionReport/items/QuestionNotes.vue';
import { isPdf } from '@/ui/helpers/systemHelper';
import { isLowQualityImagesFromReportPageKey } from '@/ui/injectionKeys';
import { LegacyFormParamResponse } from '@/ui/components/inspectionReport/legacyTypes';
import { getScore } from '@/ui/helpers/inspectionHelper';

export const List = defineComponent<ItemProps>({
  name: 'List',
  props: ['item'],
  setup(props) {
    const isLowQualityImages = inject(isLowQualityImagesFromReportPageKey, false);
    const { resource, isTranslationEnabled } = useInspectionReport();
    const findResponseSetByUuid = ({ uuid }: { uuid: string}) => (uuid === props.item.params.response_set);

    const responses = computed(() => {
      if (props.item.response && resource.value && resource.value.form) {
        // Somewhere along the way we are sending the response as object, array or string.
        // We need to check these cases here. Please, talk with Damir (Project Manager)
        // to ask if a task where each client (iOS, webapp and Android) sends
        // a key explaining where this inspection was performed
        if (Array.isArray(props.item.response)) {
          const itemResponses = (props.item.response ? props.item.response as Array<string> : []);

          return resource.value.form.params.response_sets
            .find(findResponseSetByUuid)
            ?.responses
            .filter(({ uuid }) => (itemResponses.includes(uuid))) ?? [];
        }

        if (typeof props.item.response === 'string') {
          return resource.value.form.params.response_sets
            .find(findResponseSetByUuid)
            ?.responses
            .filter(({ uuid }) => (props.item.response === uuid)) ?? [];
        }

        if (isObject(props.item.response)) {
          return resource.value.form.params.response_sets
            .find(findResponseSetByUuid)
            ?.responses
            .filter(({ uuid }) => Object.values(props.item.response as Record<string, unknown>).includes(uuid)) ?? [];
        }
      }

      return [];
    });

    const displayScore = computed(() => {
      if (!resource.value?.template) {
        return false;
      }

      return resource.value.template.display_score
        && typeof (props.item.score) !== 'undefined'
        && typeof (props.item.max_score) !== 'undefined'
        && props.item.max_score !== 0
        && hasFeature('scores');
    });

    const getResponseTitle = (response: LegacyFormParamResponse, isLast: boolean) => {
      return isTranslationEnabled && response.title_translated
        ? (isLast ? response.title_translated : `${response.title_translated},`)
        : (isLast ? response.title : `${response.title},`);
    };

    return function render() {
      return h('tbody', [
        h('tr', [
          h(TableBox, {
            props: {
              colspan: 4,
            },
          }, [
            props.item.image_id || props.item.image
              ? h(ImageAsset, {
                class: 'mb-2 inspection-report__question-img',
                props: {
                  photo: props.item,
                  isLowQualityImages: isPdf && isLowQualityImages,
                },
              })
              : null,
            h('span', { class: 'font-semibold' }, [h(QuestionTitle, { props: { question: props.item } })]),
            h(DocumentAsset, { props: { item: props.item } }),
          ]),
          h(TableBox, {
            props: {
              colspan: displayScore.value ? 3 : 4,
            },
          }, [
            props.item.responded
              ? h('div', { class: 'inspection-report__response' }, [
                ...responses.value.map((response, index) => h('div', {
                  class: 'inspection-report__response-container',
                }, [
                  h('div', {
                    class: 'inspection-report__response-bullet',
                    attrs: { style: `background-color:${response.color}` },
                  }),
                  h('span', { class: 'inspection-report__response-title' }, [
                    getResponseTitle(response, index + 1 === responses.value.length),
                  ]),
                ])),
              ])
              : '-',
          ]),
          displayScore.value
            ? h(TableBox, {
              props: {
                appendClass: 'inspection-report__table-row-right',
                colspan: 1,
              },
            }, t('inspection-report.score-points', { score: getScore(props.item.score) }))
            : null,
        ]),
        props.item.notes
          ? h('tr', [
            h(TableBox, {
              props: {
                colspan: 8,
              },
            }, [
              h(QuestionNotes, { props: { notes: props.item.notes } }),
            ]),
          ])
          : null,
        props.item.photos && props.item.photos.length > 0
          ? h('tr', [
            h(TableBox, {
              props: {
                colspan: 8,
              },
            }, [
              h('div', { class: 'flex pl-2 report-list-question-photo' }, [
                props.item.photos.map(photo => h('div', { class: 'flex items-top' }, [
                  h('span', { class: 'mr-1 pt-1' }, [`${photo.index?.toString()}.`]),
                  h(ImageAsset, {
                    class: 'mr-4 inline-block',
                    props: {
                      photo,
                      isThumbnail: true,
                      isLowQualityImages: isPdf && isLowQualityImages,
                    },
                  }),
                ])),
              ]),
            ]),
          ])
          : null,
      ]);
    };
  },
});
